import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Flex,
  IconButton,
  Input,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import { useMutation, useQuery } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";

import MoblyzeApolloClient from "../services/MoblyzeApolloClient";
import {
  CLIENT_SET_JOB_NOTIFICATION_SETTINGS_MUTATION,
  JOB_APPLICANT_EMAILS_QUERY,
  JOB_SHORTLIST_EMAILS_QUERY,
  SET_JOB_APPLICANT_EMAILS_MUTATION,
  SET_JOB_SHORTLIST_EMAILS_MUTATION,
} from "../graphql/jobs";
import { CURRENT_USER_QUERY } from "../graphql/users";
import { ACCOUNT_PATH } from "../routes";

const FREQUENCY_LABELS = {
  EveryApplicant: "Every applicant",
  DailyDigest: "Daily summary",
  Off: "Off",
};
const MAX_RECIPIENTS = 10;
const DEFAULT_DAILY_HOUR = 8;
const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`,
}));

// Per-job email notification settings. For a recruiter on a job whose agency
// gets its emails from the API (applicantEmails non-null), just a switch for
// emailing each new applicant to the job's owner; the recruiter's other
// emails are set on the account page. For a client on such a job
// (shortlistEmails non-null), just a switch for the one email the client
// gets, each shortlisted candidate, sent to all the client's members.
// Otherwise the full dialog for new-applicant emails: frequency (Daily is the
// default), preferred local delivery hour for the daily digest, and who
// receives the applications (up to 10 addresses, prefilled with the
// signed-in user's email).
export default function ClientJobNotificationSettings({ moblyzeJob }) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: currentUserData } = useQuery(CURRENT_USER_QUERY);
  const isRecruiter = currentUserData?.currentUser?.role === "Recruiter";
  const isClient = currentUserData?.currentUser?.role === "Client";
  const clientOrgName = currentUserData?.currentUser?.clientOrganization?.name;

  // Null unless this is a recruiter on a job whose agency gets its emails
  // from the API (and while loading, or against an API without the field),
  // in which case the full dialog shows.
  const { data: applicantEmailsData, loading: loadingApplicantEmails } =
    useQuery(JOB_APPLICANT_EMAILS_QUERY, {
      variables: { id: moblyzeJob?.id },
      skip: !isRecruiter || !moblyzeJob?.id,
    });
  const [setApplicantEmails, { loading: savingApplicantEmails }] = useMutation(
    SET_JOB_APPLICANT_EMAILS_MUTATION
  );
  const applicantEmails = applicantEmailsData?.moblyzeJob?.applicantEmails;
  const owner = applicantEmailsData?.moblyzeJob?.owner;

  const onApplicantEmailsChange = (enabled) => {
    setApplicantEmails({
      variables: { moblyzeJobId: moblyzeJob.id, enabled },
    }).catch((e) => {
      toast({
        title: "Couldn't update new-applicant emails",
        description: e?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    });
  };

  // Null unless this is one of the job's client members and the job's agency
  // gets its emails from the API (and while loading, or against an API
  // without the field), in which case the full dialog shows.
  const { data: shortlistEmailsData, loading: loadingShortlistEmails } =
    useQuery(JOB_SHORTLIST_EMAILS_QUERY, {
      variables: { id: moblyzeJob?.id },
      skip: !isClient || !moblyzeJob?.id,
    });
  const [setShortlistEmails, { loading: savingShortlistEmails }] = useMutation(
    SET_JOB_SHORTLIST_EMAILS_MUTATION
  );
  const shortlistEmails = shortlistEmailsData?.moblyzeJob?.shortlistEmails;

  const onShortlistEmailsChange = (enabled) => {
    setShortlistEmails({
      variables: { moblyzeJobId: moblyzeJob.id, enabled },
    }).catch((e) => {
      toast({
        title: "Couldn't update shortlist emails",
        description: e?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    });
  };

  const [savedSettings, setSavedSettings] = useState(
    moblyzeJob?.clientNotificationSettings || null
  );
  const [frequency, setFrequency] = useState("DailyDigest");
  const [dailySendHour, setDailySendHour] = useState(DEFAULT_DAILY_HOUR);
  const [recipients, setRecipients] = useState([""]);

  const openPanel = () => {
    setFrequency(savedSettings?.frequency || "DailyDigest");
    setDailySendHour(savedSettings?.dailySendHour ?? DEFAULT_DAILY_HOUR);
    const existing = (savedSettings?.recipientEmails || []).filter(Boolean);
    setRecipients(
      existing.length ? existing : [currentUserData?.currentUser?.email || ""]
    );
    onOpen();
  };

  // Auto-open the panel when the recipient follows the email "mute" CTA
  // (…/jobs/:id?notificationSettings=open). Reads window.location so it works
  // on both the agency and client job pages.
  //
  // Guarded to fire once. A recruiter waits for the applicant-emails query
  // (a client for the shortlist-emails one) to settle, to know which panel
  // to open; the switch panel then opens at once.
  // The full dialog is deferred until the recipient prefill is ready:
  // on a cold deep-link from an email CURRENT_USER_QUERY is often still in
  // flight at mount, and opening early would seed Recipient 1 empty and never
  // repopulate. Wait until we have either saved recipients or the current
  // user's email before opening. currentUserData in the deps makes the effect
  // re-run when the query resolves.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("notificationSettings") !== "open") return;
    if (isRecruiter && loadingApplicantEmails) return;
    if (isClient && loadingShortlistEmails) return;
    if (applicantEmails || shortlistEmails) {
      autoOpenedRef.current = true;
      onOpen();
      return;
    }

    const hasSavedRecipients = (savedSettings?.recipientEmails || []).some(
      Boolean
    );
    const currentUserEmail = currentUserData?.currentUser?.email;
    if (!hasSavedRecipients && !currentUserEmail) return; // prefill not ready yet

    autoOpenedRef.current = true;
    openPanel();
    // openPanel reads savedSettings + currentUserData, both in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUserData,
    savedSettings,
    loadingApplicantEmails,
    applicantEmails,
    loadingShortlistEmails,
    shortlistEmails,
  ]);

  const onSave = () => {
    const recipientEmails = recipients.map((e) => e.trim()).filter(Boolean);
    const isDaily = frequency === "DailyDigest";
    const input = {
      frequency,
      dailySendHour: isDaily ? Number(dailySendHour) : null,
      timezone: isDaily
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : null,
      recipientEmails,
    };
    MoblyzeApolloClient.mutate({
      mutation: CLIENT_SET_JOB_NOTIFICATION_SETTINGS_MUTATION,
      variables: { moblyzeJobId: moblyzeJob.id, input },
    })
      .then(() => {
        setSavedSettings(input);
        toast({
          title: "Notification settings saved",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose();
      })
      .catch((e) => {
        toast({
          title: "Couldn't save notification settings",
          description: e?.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };

  const savedFrequency = savedSettings?.frequency || "DailyDigest";

  const renderPanel = (label, onOpenPanel, body) => (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-end">
      <PopoverTrigger>
        <Button
          colorScheme="white"
          variant="outline"
          borderColor="gray.600"
          _hover={{ borderColor: "gray.300" }}
          rightIcon={<ChevronDownIcon />}
          onClick={isOpen ? onClose : onOpenPanel}
        >
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        w="340px"
        bg="white"
        color="gray.800"
        _focus={{ boxShadow: "lg" }}
      >
        <PopoverBody p={4}>{body}</PopoverBody>
      </PopoverContent>
    </Popover>
  );

  // Which dialog: the API decides per agency. applicantEmails (for a
  // recruiter) and shortlistEmails (for a client) are null while the job's
  // agency is on the Metabase alerts, which read only the single frequency
  // below, so those jobs keep the full dialog.
  if (applicantEmails) {
    const ownerName = [owner?.firstName, owner?.lastName]
      .filter(Boolean)
      .join(" ");
    return renderPanel(
      `New applicant emails: ${applicantEmails.enabled ? "On" : "Off"}`,
      onOpen,
      <>
        <Flex align="center" justify="space-between" gap={3}>
          <Text as="label" htmlFor="applicant-emails" fontSize="sm">
            Email each new applicant on this job
          </Text>
          <Switch
            id="applicant-emails"
            colorScheme="teal"
            isChecked={applicantEmails.enabled}
            isDisabled={savingApplicantEmails}
            onChange={(e) => onApplicantEmailsChange(e.target.checked)}
          />
        </Flex>
        <Text fontSize="xs" color="gray.500" mt={2}>
          {owner?.role === "Recruiter"
            ? `Sent to the job's owner, ${ownerName || "a recruiter"}.`
            : "This job has no recruiter owner, so these emails aren't sent."}
        </Text>
        <Link
          as={RouterLink}
          to={`${ACCOUNT_PATH}#email-notifications`}
          color="teal.600"
          fontSize="sm"
          display="inline-block"
          mt={3}
        >
          Your other email notifications
        </Link>
      </>
    );
  }

  if (shortlistEmails) {
    return renderPanel(
      `Shortlist emails: ${shortlistEmails.enabled ? "On" : "Off"}`,
      onOpen,
      <>
        <Flex align="center" justify="space-between" gap={3}>
          <Text as="label" htmlFor="shortlist-emails" fontSize="sm">
            Email me each candidate shortlisted on this job
          </Text>
          <Switch
            id="shortlist-emails"
            colorScheme="teal"
            isChecked={shortlistEmails.enabled}
            isDisabled={savingShortlistEmails}
            onChange={(e) => onShortlistEmailsChange(e.target.checked)}
          />
        </Flex>
        <Text fontSize="xs" color="gray.500" mt={2}>
          {clientOrgName
            ? `Sent to everyone on your team at ${clientOrgName}.`
            : "Sent to everyone on your team."}
        </Text>
      </>
    );
  }

  // The full dialog: agencies still on Metabase, and anyone else.
  return renderPanel(
    `Notify me: ${FREQUENCY_LABELS[savedFrequency]}`,
    openPanel,
    <>
      <Text fontWeight="bold" fontSize="sm" mb={2}>
        Notify me about new applicants
      </Text>
      <RadioGroup value={frequency} onChange={setFrequency}>
        <Stack spacing={1}>
          <Radio value="EveryApplicant">Every applicant</Radio>
          {/* Reserve the row height (label + dropdown) so checking Daily
              never moves "Off" or anything below it, and center the radio
              so all three options stay equidistant. */}
          <Flex align="center" gap={3} minH="52px">
            <Radio value="DailyDigest" flexShrink={0} whiteSpace="nowrap">
              Daily summary
            </Radio>
            {frequency === "DailyDigest" && (
              <Flex direction="column" gap={1} ml="auto">
                <Text
                  fontSize="xs"
                  color="gray.400"
                  whiteSpace="nowrap"
                  lineHeight="1.2"
                >
                  Your local time
                </Text>
                <Select
                  size="sm"
                  w="120px"
                  bg="white"
                  color="gray.800"
                  aria-label="Your local time"
                  value={dailySendHour}
                  onChange={(e) => setDailySendHour(e.target.value)}
                >
                  {HOURS.map((h) => (
                    <option key={h.value} value={h.value}>
                      {h.label}
                    </option>
                  ))}
                </Select>
              </Flex>
            )}
          </Flex>
          <Radio value="Off">Off</Radio>
        </Stack>
      </RadioGroup>

      <Text fontSize="xs" color="gray.400" fontWeight="semibold" mt={4} mb={2}>
        SEND APPLICATIONS TO
      </Text>
      <Stack spacing={2}>
        {recipients.map((email, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Flex key={i} gap={2} align="center">
            <Input
              size="sm"
              variant="light"
              type="email"
              aria-label={`Recipient ${i + 1}`}
              value={email}
              onChange={(e) =>
                setRecipients(
                  recipients.map((r, j) => (j === i ? e.target.value : r))
                )
              }
            />
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={`Remove recipient ${i + 1}`}
              icon={<CloseIcon boxSize={2.5} />}
              onClick={() =>
                setRecipients(recipients.filter((_, j) => j !== i))
              }
            />
          </Flex>
        ))}
      </Stack>
      <Button
        variant="link"
        colorScheme="teal"
        size="sm"
        mt={2}
        isDisabled={recipients.length >= MAX_RECIPIENTS}
        onClick={() => setRecipients([...recipients, ""])}
      >
        + Add recipient
      </Button>

      <Flex justify="flex-end" gap={2} mt={4}>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" colorScheme="teal" onClick={onSave}>
          Save
        </Button>
      </Flex>
    </>
  );
}
